/**
 * Loading skeleton components with shimmer animation.
 * Provides base Skeleton + pre-built variants for BoxCard, BoxList, BoxDetail.
 */

// ── Base Skeleton ──────────────────────────────────────────────

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div
      className={`animate-shimmer motion-reduce:animate-none rounded bg-surface bg-gradient-to-r from-surface via-border/50 to-surface bg-[length:400%_100%] ${className}`}
      aria-hidden="true"
    />
  );
}

// ── BoxCard Skeleton ───────────────────────────────────────────

export function BoxCardSkeleton() {
  return (
    <div
      className="w-full rounded-xl border border-border bg-surface p-4 space-y-3"
      aria-hidden="true"
    >
      {/* Header row: name + badge */}
      <div className="flex items-start justify-between gap-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-8" />
      </div>
      {/* Location */}
      <div className="flex items-center gap-1">
        <Skeleton className="h-3.5 w-3.5 rounded-full" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      {/* Date */}
      <Skeleton className="h-3 w-1/3" />
    </div>
  );
}

// ── BoxList Skeleton (grid of cards) ───────────────────────────

interface BoxListSkeletonProps {
  count?: number;
}

export function BoxListSkeleton({ count = 6 }: BoxListSkeletonProps) {
  return (
    <div className="space-y-4" role="status" aria-busy="true" aria-label="Loading boxes">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-8 w-28" />
      </div>
      {/* Search bar skeleton */}
      <Skeleton className="h-10 w-full rounded-lg" />
      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {Array.from({ length: count }, (_, i) => (
          <BoxCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

// ── BoxDetail Skeleton ─────────────────────────────────────────

export function BoxDetailSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-busy="true" aria-label="Loading box details">
      {/* Back button */}
      <Skeleton className="h-4 w-28" />

      {/* Box header */}
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-7 w-2/3" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-5 w-20 rounded" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <Skeleton className="h-8 w-16 rounded-lg" />
      </div>

      {/* Notes */}
      <div className="rounded-lg border border-border bg-surface p-3">
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4" />
      </div>

      {/* Items section */}
      <div>
        <Skeleton className="h-4 w-20 mb-3" />
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="flex items-center gap-3 py-2">
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-8" />
              <Skeleton className="h-4 w-4 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
